import {
  FIELD_BOOKING_CATEGORY_ID_KEY,
  FIELD_CUSTOM_FIELDS_KEY,
  FIELD_PARENT_ACTIVITY_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { useBookingCategoryLink } from "PFApp/hooks/activity_form/use_booking_category_link";
import { useGetCustomFieldsToLink } from "PFApp/hooks/activity_form/use_get_custom_fields_to_link";
import { activityCustomTypesAccessLevels } from "PFCore/helpers/custom_types";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { AccessLevel, ActivityCustomField } from "PFTypes";
import { useWatch } from "react-hook-form";

import { useActivityFormStore } from "../../activity_form_store";
import { useEngagementTemplate } from "../use_engagement_template";
import { useSubtemplate } from "../use_subtemplate";
import { areValuesEqual } from "./helpers";

export const useEngagementLinkStatus = () => {
  const { data: currentProfile } = useCurrentProfile();
  const activityCreator = useActivityFormStore((state) => state.activityCreator);
  const template = useActivityFormStore((state) => state.template);
  const subtemplate = useSubtemplate();
  const parentTemplate = useEngagementTemplate();
  const getLinkedCustomFields = useGetCustomFieldsToLink({ template, parentTemplate });
  const [selectedParentActivity, formCustomFields, bookingCategoryId] = useWatch({
    name: [FIELD_PARENT_ACTIVITY_KEY, FIELD_CUSTOM_FIELDS_KEY, FIELD_BOOKING_CATEGORY_ID_KEY]
  });
  const { isPropertyLinked: isBookingCategoryLinkable, getBookingCategoryLinkHelpers } =
    useBookingCategoryLink({ template });

  if (!selectedParentActivity) {
    return { hasAnyLinkedValue: false, hasAnyValueChanged: false };
  }

  const isValueChanged = (linkedCustomField: ActivityCustomField) => {
    const customFieldValues = formCustomFields[linkedCustomField.type.name];

    return !areValuesEqual(customFieldValues || [], linkedCustomField.values);
  };

  const linkedCustomFields = getLinkedCustomFields({
    engagement: selectedParentActivity,
    roleSubtemplate: subtemplate
  });
  const { canLink: canLinkBookingCategory, isLinked: isBookingCategoryLinked } =
    getBookingCategoryLinkHelpers({
      parentActivity: selectedParentActivity,
      activityCreator,
      subtemplate,
      bookingCategoryId
    });

  const hasAnyLinkedValue =
    linkedCustomFields.some(({ values }) => values.length > 0) || isBookingCategoryLinkable;

  const hasBookingCategoryChanged = !isBookingCategoryLinked && canLinkBookingCategory;
  const hasAnyValueChanged = linkedCustomFields.some(isValueChanged) || hasBookingCategoryChanged;

  const isPermittedToRestore =
    hasBookingCategoryChanged ||
    linkedCustomFields.some((linkedCustomField: ActivityCustomField) => {
      const accessLevel = activityCustomTypesAccessLevels(currentProfile, activityCreator)[
        linkedCustomField.type.id
      ];

      return isValueChanged(linkedCustomField) && accessLevel === AccessLevel.ReadWrite;
    });

  return { hasAnyLinkedValue, hasAnyValueChanged, isPermittedToRestore };
};
